import { createPerfilApiClient } from './perfilApiClient';
import { toObject, toArray } from './_normalize';

/**
 * Cambio de Asiento (Butaca o Plaza) de Abonado — Service
 *
 * Métodos puros para todas las llamadas HTTP del flujo de cambio de asiento.
 * Usa `createPerfilApiClient()` para autenticación y gestión de cabeceras.
 *
 * Contrato de retorno:
 *  - Todos los métodos devuelven datos ya normalizados (via `_normalize.js`).
 *  - Los errores NO se capturan aquí; se propagan al composable que los
 *    convierte con `toError()` para exponer `loading/error` consistentes.
 *
 * Endpoints base: /f/perfilV2/abonados/{id}/cambio-butaca/...
 * (LEGACY: los endpoints backend aún usan el prefijo `cambio-butaca`.
 *  Se mantienen tal cual hasta que backend migre a `cambio-asiento`.)
 * Borrado de sesión:  DELETE /f/sesion/{session_id}
 */

const BASE = abonadoId => `/f/perfilV2/abonados/${abonadoId}/cambio-butaca`; // LEGACY: backend endpoint path, mantener hasta migración

/**
 * Lee el api_key del localStorage en el momento de la llamada.
 * @returns {string|null}
 */
function getApiKey() {
    return localStorage.getItem('token');
}

/**
 * Lee el hash_teatro del localStorage.
 * @returns {string|null}
 */
function getHashTeatro() {
    return localStorage.getItem('hash_teatro');
}

export default {
    /**
     * Consulta el estado de la sesión de cambio de butaca.
     *
     * Endpoint POST-operación: devuelve el estado de la sesión, la transacción
     * asociada y el historial del cambio (old/new).
     *
     * GET /f/perfilV2/abonados/{id}/cambio-butaca/info?session_id=&tpv_order= // LEGACY: backend endpoint path
     *
     * @param {string|number} abonadoId
     * @param {string} [sessionId] — ID de sesión activa (requerido post-pago)
     * @param {string} [tpvOrder] — Orden TPV asociada (opcional)
     * @returns {Promise<Object>} Datos del cambio: cambio_id, n_pedido, tpv_order, historial, etc.
     */
    async getInfo(abonadoId, sessionId = null, tpvOrder = null) {
        const client = createPerfilApiClient();
        const params = { api_key: getApiKey() };
        if (sessionId) params.session_id = sessionId;
        if (tpvOrder) params.tpv_order = tpvOrder;
        const response = await client.get(`${BASE(abonadoId)}/info`, { params });
        return toObject(response.data);
    },

    /**
     * Obtiene las zonas disponibles para el cambio de butaca.
     *
     * GET /f/zonas?recinto_id={recintoId}
     *
     * @param {string|number} abonadoId  ID del abonado (no se usa, se mantiene por compatibilidad)
     * @param {string|number} [recintoId]  ID del recinto para filtrar zonas
     * @returns {Promise<Array>}
     */
    async getZonas(abonadoId, recintoId) {
        const client = createPerfilApiClient();
        const params = { api_key: getApiKey() };
        if (recintoId) {
            params.recinto_id = recintoId;
        }
        const response = await client.get('/f/zonas', { params });
        return toArray(response.data).map(z => ({
            ...z,
            available:    z.available ?? Boolean(z.esta_disponible),
            name:         z.name      ?? z.nombre ?? z.descripcion ?? '',
            price:        z.price     ?? z.precio ?? null,
            availability: z.availability ?? z.libres ?? null,
        }));
    },

    /**
     * Obtiene las REGIONES ANEXAS del recinto para el cambio de asiento.
     *
     * GET /f/temporadas/{listaAbonadoId}/recintos/{recintoId}/zonas/anexas
     * (api-routes.js: 'front.temporada.recinto.regiones.anexas')
     *
     * Las anexas son zonas sin butacas numeradas (plazas/parking) que el
     * endpoint `/f/zonas` NO devuelve. El paso Zona las UNIFICA con las zonas
     * normales (patrón legacy `SeleccionarRegion.getRegionesAnexas`), de modo
     * que recintos que solo tienen anexas dejen de mostrar la tabla vacía.
     *
     * Normaliza al mismo formato que `getZonas` (id/name/available/price/
     * availability) y además deriva `precios` aplanando `tipos_abonos[].descuentos`
     * (patrón legacy `SeleccionarRegion.vue:127-132`). Preserva los campos raw
     * vía spread (`tiene_plazas`, `with_seats`, `is_parking`, `tipos_abonos`, ...).
     *
     * @param {string|number} abonadoId       ID del abonado (no se usa, se mantiene por compatibilidad)
     * @param {string|number} recintoId       ID del recinto
     * @param {string|number} listaAbonadoId  ID de temporada (lista de abonados)
     * @param {string}        hashTeatro      Hash del teatro para el param tid
     * @param {boolean}       [renovacion=false] Si true, el backend devuelve sólo precios de renovación
     * @returns {Promise<Array>}
     */
    async getZonasAnexas(abonadoId, recintoId, listaAbonadoId, hashTeatro, renovacion = false) {
        const client = createPerfilApiClient();
        const response = await client.get(
            `/f/temporadas/${listaAbonadoId}/recintos/${recintoId}/zonas/anexas`,
            // Misma convención 1/0 que la rama butacas de `getAsientos` (L199).
            { params: { api_key: getApiKey(), tid: hashTeatro, renovacion: renovacion ? 1 : 0 } }
        );
        return toArray(response.data).map(z => ({
            ...z,
            available:    z.available ?? Boolean(z.esta_disponible),
            name:         z.name      ?? z.nombre ?? z.descripcion ?? '',
            price:        z.price     ?? z.precio ?? null,
            availability: z.availability ?? z.libres ?? null,
            // Patrón legacy: `zona.precios = concat(tipos_abonos[].descuentos)`.
            precios: (Array.isArray(z.tipos_abonos) ? z.tipos_abonos : [])
                .reduce((acc, tipoAbono) => acc.concat(
                    Array.isArray(tipoAbono?.descuentos) ? tipoAbono.descuentos : []
                ), []),
        }));
    },

    /**
     * Obtiene las butacas disponibles en una zona concreta.
     *
     * GET /f/temporadas/{listaAbonadoId}/recintos/{recintoId}/zonas/{zonaId}/butacas
     *
     * @param {string|number} abonadoId  ID del abonado
     * @param {string|number} recintoId  ID del recinto
     * @param {string|number} zonaId     ID de la zona/región
     * @param {string|number} listaAbonadoId  ID de temporada (lista de abonados)
     * @param {string}        hashTeatro  Hash del teatro para el param tid
     * @param {number|null}   [tipoSvg]  Tipo de SVG del recinto (1=asientos normales, 2=Leaflet 3D)
     * @param {number|null}   [tipoProcesoOnline] Tipo de proceso online (1=regiones, 2=secciones, 3=mapa general)
     * @param {boolean}       [renovacion] Si true, el backend devuelve sólo precios de renovación
     * @param {boolean}       [tienePlazas] Si la zona tiene plazas no numeradas (tiene_plazas). Para
     *        `tipo_svg === 1` + `tipo_proceso_online === 1` bifurca entre grid antifluo
     *        (`false`) y butacas DettallesButacas + SVG de región (`true`, patrón legacy
     *        `SeleccionarAsientosRegionAnexa`).
     * @returns {Promise<Object>} Datos de asientos con configuracion_asientos
     */
    async getAsientos(abonadoId, recintoId, zonaId, listaAbonadoId, hashTeatro, tipoSvg, tipoProcesoOnline, renovacion, tienePlazas) {
        // ── Antifluo endpoint path (tipo_svg=1 + sin secciones + sin plazas) ──
        // Para `tipo_svg === 1` + `tipo_proceso_online !== 2` usamos el endpoint
        // antifluo SOLO cuando la zona NO tiene plazas (`tiene_plazas` falsy):
        // el grid de asientos normales consume `configuracion`/`precios`.
        // Las zonas con plazas (`tiene_plazas=true`, proceso 1) van al path
        // default de butacas (DettallesButacas) + SVG de la región via
        // `getSvgZona`, mismo patrón que el legacy `SeleccionarAsientosRegionAnexa`.
        // El proceso 2 (secciones con plazas) SIEMPRE va a butacas (el `!== 2`
        // excluye el antifluo y el default cubre butacas).
        if (tipoSvg === 1 && tipoProcesoOnline !== 2 && !tienePlazas) {
            const client = createPerfilApiClient();
            const response = await client.get('/f/zonas/' + zonaId, {
                params: {
                    api_key: getApiKey(),
                    conf: true,
                    zona_id: zonaId,
                    tid: hashTeatro,
                    // Si el descuento del abonado es de renovación, pedimos
                    // al backend sólo los precios de renovación. El cliente
                    // también filtra (tiposAbonosDisponibles) pero este
                    // param es la fuente de verdad — el backend decide qué
                    // precios incluir en la respuesta.
                    renovacion: renovacion ? 1 : 0,
                }
            });
            const rawConfig = response.data.data.configuracion || {};
            const precios = response.data.data.precios || [];
            const configuracion_asientos = {};

            for (const [filaKey, asientos] of Object.entries(rawConfig)) {
                const lista = Array.isArray(asientos) ? asientos : [];
                let huecoIdx = 0;
                configuracion_asientos[filaKey] = lista.map(a => {
                    // Patrón canónico sdcompostela: el endpoint antifluo envía
                    // `asiento: 0 | null | undefined` para plazas no numeradas.
                    // La plaza NO se pinta con un "0", pero el slot en el grid
                    // se conserva para respetar la distribución visual de la fila.
                    // Se marca con `es_hueco: true` y se deja en el array —
                    // `SelectorAsientosComponent`/`AsientoComponent` la renderiza
                    // como celda del mismo tamaño y `onSelectorAsientosToggle`
                    // la salta para no emitir selección.
                    const esHueco = a.asiento === undefined
                        || a.asiento === null
                        || a.asiento === 0;
                    const asientoNro = esHueco ? null : a.asiento;
                    const id = a.id ?? (esHueco
                        ? `${filaKey}-hueco-${huecoIdx++}`
                        : `${filaKey}-${a.asiento ?? a.numero ?? ''}`);
                    // Defensa per-seat (compat con orquestador): si el seat es
                    // hueco y la zona no marca `tiene_plazas`, lo seguimos
                    // marcando como plaza para que el bifurcador per-seat ascienda
                    // a Camino 2 en el orquestador.
                    const esPlaza = esHueco;
                    return {
                        fila: a.fila ?? filaKey,
                        asiento: asientoNro,
                        ocupado: esHueco ? 0 : (a.ocupado ?? 0),
                        id,
                        // `plaza_id` se usa en el toggle de CambioAsiento y reserva.
                        plaza_id: id,
                        es_hueco: esHueco,
                        es_plaza: esPlaza,
                        svg_id: a.svg_id ?? null,
                        sector_name: a.sector_name ?? a.sector ?? null,
                        // Flag de renovación propagado del backend. Permite al front
                        // validar/filtrar la butaca contra el descuento.renovacion
                        // del abonado (e.g. ocultar butacas de renovación a un
                        // abonado sin descuento de renovación).
                        renovacion: a.renovacion ?? null,
                        // Bandera de selección: la añade/quita el toggle del front.
                        // Para huecos nunca se selecciona.
                        seleccionado: false,
                        // Huecos no tienen precios asociados.
                        precios_count: esHueco ? 0 : precios.length,
                        precios: esHueco ? [] : precios,
                    };
                });
            }

            return {
                configuracion_asientos,
                tipo_recinto: response.data.data.tipo_recinto ?? null,
                tipos_abonos: response.data.data.tipos_abonos ?? [],
            };
        }

        // ── Default path (tipo_svg=2, proceso 2, o proceso 1 con plazas) ──
        const client = createPerfilApiClient();
        const response = await client.get(
            `/f/temporadas/${listaAbonadoId}/recintos/${recintoId}/zonas/${zonaId}/butacas`,
            // Si el descuento del abonado es de renovación, pedimos al backend sólo
            // los precios de renovación. Misma convención 1/0 que el path antifluo.
            { params: { api_key: getApiKey(), renovacion: renovacion ? 1 : 0, tid: hashTeatro } }
        );
        // La respuesta es DettallesButacas[] (array plano de butacas)
        // Cada butaca tiene: id, fila, butaca (número en la fila), libre, precios, etc.
        const butacas = toArray(response.data);
        const configuracion_asientos = {};
        butacas.forEach(b => {
            const fila = b.fila ?? 'default';
            if (!configuracion_asientos[fila]) {
                configuracion_asientos[fila] = [];
            }
            configuracion_asientos[fila].push({
                fila,
                // Usar butaca (label del asiento en la fila), con fallback a numero (plazas)
                asiento: b.butaca ?? b.numero,
                ocupado: b.libre ? 0 : 1,
                id: b.id,
                // Flag para distinguir butaca numerada vs plaza sin numerar
                // b.butaca existe → numerada → Camino 1 (region_id+row+seat)
                // b.butaca es null → plaza → Camino 2 (plaza_id)
                es_plaza: !b.butaca,
                // Datos adicionales para el SVG seat selector
                label: b.butaca ? `Fila ${b.fila} - Butaca ${b.butaca}` : null,
                // Usar `sector_name` como nombre canónico (alineado con tipo_svg=2).
                sector_name: b.sector_name ?? b.sector ?? null,
                plaza_id: b.id, // El id de la butaca/plaza del backend
                svg_id: b.svg_id ?? null,
                // Flag de renovación propagado del backend (por butaca). Permite al
                // front validar/filtrar contra el descuento.renovacion del abonado.
                renovacion: b.renovacion ?? null,
                precios_count: b.precios?.length ?? 0,
                precios: b.precios?.map(p => ({
                    id: p.id,
                    descripcion: p.descripcion,
                    descuentos: p.descuentos,
                    renovacion: p.renovacion ?? null,
                })) ?? [],
            });
        });
        return {
            configuracion_asientos,
            tipo_recinto: null,
        };
    },

    /**
     * Obtiene el SVG de una zona concreta (mapa 3D de butacas).
     *
     * GET /f/temporadas/{listaAbonadoId}/recintos/{recintoId}/zonas/{zonaId}/svg
     *
     * @param {string|number} recintoId
     * @param {string|number} zonaId
     * @param {string|number} listaAbonadoId
     * @param {string}        hashTeatro  Hash del teatro para el param tid
     * @returns {Promise<string>}  String SVG del mapa de butacas
     */
    async getSvgZona(recintoId, zonaId, listaAbonadoId, hashTeatro) {
        const client = createPerfilApiClient();
        const response = await client.get(
            `/f/temporadas/${listaAbonadoId}/recintos/${recintoId}/zonas/${zonaId}/svg`,
            { params: { api_key: getApiKey(), tid: hashTeatro } }
        );
        return response.data?.svg ?? response.data?.data?.svg ?? '';
    },

    /**
     * Obtiene el SVG del recinto (mapa general) para `tipo_proceso_online=3`.
     *
     * Wrapper sobre `getSvgRecinto` para mantener contrato explícito en el
     * adapter de mapa general. Reutiliza el endpoint existente
     * `GET /f/recinto/{recinto_id}`.
     *
     * @param {string|number} recintoId
     * @returns {Promise<string>}  String SVG del mapa del recinto
     */
    async getRecintoSvg(recintoId) {
        return this.getSvgRecinto(recintoId);
    },

    /**
     * Obtiene las butacas del recinto completo (sin filtro de zona) para
     * `tipo_proceso_online=3` (camino 'mapa-general').
     *
     * Replica el shape de `getAsientos(...)` en su path `tipo_svg=1/2` por
     * defecto, pero sin requerir `zonaId`. Devuelve `configuracion_asientos`
     * con todas las butacas del recinto, indexadas por `fila`.
     *
     * GET /f/temporadas/{listaAbonadoId}/recintos/{recintoId}/butacas
     *
     * NOTA: el endpoint exacto debe confirmarse runtime con backend
     * (pregunta abierta en design D7 / obs 2818 rev 2 § 10). La forma
     * de retorno es la misma que el path default de `getAsientos` cuando
     * se omite `zonaId`.
     *
     * @param {string|number} abonadoId
     * @param {string|number} recintoId
     * @param {string|number} listaAbonadoId
     * @param {string}        hashTeatro  Hash del teatro para el param tid
     * @param {boolean}       [renovacion=false] Si true, el backend devuelve sólo precios de renovación
     * @returns {Promise<Object>} Datos de asientos con configuracion_asientos
     */
    async getButacasByRecinto(abonadoId, recintoId, listaAbonadoId, hashTeatro, renovacion = false) {
        const client = createPerfilApiClient();
        const response = await client.get(
            `/f/temporadas/${listaAbonadoId}/recintos/${recintoId}/butacas`,
            { params: { api_key: getApiKey(), renovacion: renovacion ? 1 : 0, tid: hashTeatro } }
        );
        const butacas = toArray(response.data);
        const configuracion_asientos = {};
        butacas.forEach(b => {
            const fila = b.fila ?? 'default';
            if (!configuracion_asientos[fila]) {
                configuracion_asientos[fila] = [];
            }
            configuracion_asientos[fila].push({
                fila,
                asiento: b.butaca ?? b.numero,
                ocupado: b.libre ? 0 : 1,
                id: b.id,
                es_plaza: !b.butaca,
                label: b.butaca ? `Fila ${b.fila} - Butaca ${b.butaca}` : null,
                sector_name: b.sector_name ?? b.sector ?? null,
                plaza_id: b.id,
                svg_id: b.svg_id ?? null,
                // Flag de renovación propagado del backend (por butaca).
                renovacion: b.renovacion ?? null,
                precios_count: b.precios?.length ?? 0,
                precios: b.precios?.map(p => ({
                    id: p.id,
                    descripcion: p.descripcion,
                    descuentos: p.descuentos,
                    renovacion: p.renovacion ?? null,
                })) ?? [],
            });
        });
        return {
            configuracion_asientos,
            tipo_recinto: null,
        };
    },

    /**
     * Reserva la butaca seleccionada y crea la sesión de cambio.
     *
     * POST /f/perfilV2/abonados/{id}/cambio-butaca/reservar // LEGACY: backend endpoint path
     *
     * @param {string|number} abonadoId
     * @param {Object} payload
     * @param {string|number} payload.zona_id
     * @param {Object} payload.asiento — Datos del asiento seleccionado
     * @returns {Promise<{ session_id: string, session_ends_at: string, precio_total: number, jet_id: string|null }>}
     */
    async reservar(abonadoId, payload) {
        const client = createPerfilApiClient();
        const response = await client.post(
            `${BASE(abonadoId)}/reservar`,
            payload,
            { params: { api_key: getApiKey() } }
        );
        return toObject(response.data);
    },

    /**
     * Confirma el cambio de butaca.
     *
     * POST /f/perfilV2/abonados/{id}/cambio-butaca/confirmar // LEGACY: backend endpoint path
     *
     * @param {string|number} abonadoId
     * @param {Object} payload
     * @param {string} payload.session_id — ID de sesión del bloqueo activo
     * @returns {Promise<{ n_pedido: string|number, session_id: string }>}
     */
    async confirmar(abonadoId, payload) {
        const client = createPerfilApiClient();
        const response = await client.post(
            `${BASE(abonadoId)}/confirmar`,
            payload,
            { params: { api_key: getApiKey() } }
        );
        return toObject(response.data);
    },

    /**
     * Obtiene el SVG del recinto (mapa de zonas) para mostrarlo en el paso Zona.
     *
     * GET /f/recinto/{recinto_id}
     *
     * @param {string|number} recintoId  ID del recinto
     * @returns {Promise<string>}  String SVG del mapa del recinto
     */
    async getSvgRecinto(recintoId) {
        const client = createPerfilApiClient();
        const hashTeatro = localStorage.getItem('hash_teatro');
        const response = await client.get(`/f/recinto/${recintoId}`, {
            params: {
                api_key: getApiKey(),
                tid: hashTeatro,
            },
        });
        return response.data?.svg ?? response.data?.data?.svg ?? '';
    },

    /**
     * Borra la sesión activa del backend para liberar la butaca bloqueada.
     * Debe llamarse cuando el usuario navega hacia atrás o el temporizador expira.
     *
     * DELETE /f/sesion/{session_id}
     *
     * @param {string} sessionId Identificador de sesión retornado por reservar
     * @returns {Promise<void>}
     */
    async borrarSesion(sessionId) {
        const client = createPerfilApiClient();
        await client.delete(`/f/sesion/${sessionId}`, {
            params: { api_key: getApiKey() },
        });
    },

    /**
     * Cancela una reserva activa de cambio de butaca y libera la butaca.
     *
     * POST /f/perfilV2/abonados/{id}/cambio-butaca/cancelar // LEGACY: backend endpoint path
     *
     * @param {string|number} abonadoId
     * @param {string} sessionId Identificador de sesión retornado por reservar
     * @returns {Promise<Object>}
     */
    async cancelarReserva(abonadoId, sessionId) {
        const client = createPerfilApiClient();
        const response = await client.post(
            `${BASE(abonadoId)}/cancelar`,
            { session_id: sessionId },
            { params: { api_key: getApiKey() } }
        );
        return toObject(response.data);
    },
};
